import { Box, Typography } from '@mui/material'
import { useEffect, useMemo, useRef, useState } from 'react'
import { generatePaneLayoutsMap, getMultiPaneLayoutStyles } from '../../multiPane/multipPaneLayout.helper'
import { MultiPaneItem } from '../../multiPane/multiPaneLayout.interface'
import { WidgetConfig } from '../widgetDashboard.interface'
import { WidgetTypesNames } from '../widgetDashboard.enums'

interface LayoutWireframeProps {
    panes: MultiPaneItem
    widgets: Record<number, WidgetConfig<any>>
    selectedSlotId: number | null
    onSelect: (slotId: number) => void
}

const PREVIEW_HEIGHT = 280
/** Keeps the 2px hover/selected border visible on bottom panes. */
const PREVIEW_BOTTOM_PAD = 2
/** Container `border: 1` on a border-box height — layout must use the inner height. */
const PREVIEW_BORDER_Y = 0

const LayoutWireframe = ({ panes, widgets, selectedSlotId, onSelect }: LayoutWireframeProps) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const [width, setWidth] = useState(480)
    const panesConfig = useMemo(() => generatePaneLayoutsMap(panes), [panes])
    const layoutHeight = PREVIEW_HEIGHT - PREVIEW_BORDER_Y - PREVIEW_BOTTOM_PAD
    const paneStyles = useMemo(
        () => getMultiPaneLayoutStyles(panes.id, width, layoutHeight, panesConfig),
        [layoutHeight, panes.id, panesConfig, width]
    )
    const leafPanes = useMemo(
        () => Object.values(panesConfig).filter((pane) => pane.panes.length === 0),
        [panesConfig]
    )

    useEffect(() => {
        const container = containerRef.current
        if (!container) return

        const updateWidth = () => setWidth(container.clientWidth)
        updateWidth()
        const observer = new ResizeObserver(updateWidth)
        observer.observe(container)
        return () => observer.disconnect()
    }, [])

    return (
        <Box
            ref={containerRef}
            sx={{
                width: '100%',
                height: PREVIEW_HEIGHT,
                boxSizing: 'border-box',
                pb: `${PREVIEW_BOTTOM_PAD}px`,
                position: 'relative',
                overflow: 'hidden',
                bgcolor: 'background.default',
                border: 1,
                borderColor: 'divider',
            }}
        >
            {leafPanes.map((pane) => {
                const widget = widgets[pane.id]
                const selected = selectedSlotId === pane.id
                const label = widget ? WidgetTypesNames[widget.widgetType] : 'Empty'

                return (
                    <Box
                        key={pane.id}
                        component={widget ? 'button' : 'div'}
                        type={widget ? 'button' : undefined}
                        onClick={widget ? () => onSelect(pane.id) : undefined}
                        sx={{
                            ...paneStyles[pane.id],
                            position: 'absolute',
                            boxSizing: 'border-box',
                            border: 2,
                            borderColor: selected ? 'primary.main' : 'divider',
                            bgcolor: selected ? 'action.selected' : 'background.paper',
                            color: widget ? 'text.primary' : 'text.disabled',
                            cursor: widget ? 'pointer' : 'default',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            p: 1,
                            font: 'inherit',
                            '&:hover': widget ? {
                                borderColor: 'primary.main',
                                bgcolor: 'action.hover',
                            } : undefined,
                        }}
                    >
                        <Typography variant="body2" fontWeight={selected ? 500 : 400}>
                            {label}
                        </Typography>
                        {widget?.settings?.displayName && (
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: '90%' }}>
                                {widget.settings.displayName}
                            </Typography>
                        )}
                    </Box>
                )
            })}
        </Box>
    )
}

export default LayoutWireframe
